import { useTranslation } from 'react-i18next';
import { formatGetal } from '../format';
import { weeknummer } from '../week';
import type { RichtingVelden, Weekcijfer } from '../work-quality.api';

export interface RichtingstekstProps extends RichtingVelden {
  /** Het hoofdcijfer van de week ervoor; met een percentage komt "dan week 42 (31%)" erachter. */
  vorige?: Weekcijfer;
  /** `badge` maakt de richting een afgeronde pil (Resultaatblok); `tekst` (standaard) blijft tekst (Team). */
  variant?: 'tekst' | 'badge';
}

const OMHOOG = '▲';
const OMLAAG = '▼';

/**
 * De richting tegenover vorige week, zoals de dienst hem vergeleek (M6, EXPERIENCE.md Richtingstekst).
 *
 * "▲ 7 punten hoger dan week 42 (31%)", "▼ 1 punt lager", "gelijk aan week 42 (31%)" of "geen vergelijking".
 * De pijl is `aria-hidden`; het woord draagt de betekenis. Groen voor hoger, amber voor lager, gedempt voor
 * gelijk en geen vergelijking; nooit rood. Herbruikbaar op Team (story 10).
 */
export function Richtingstekst({ richting, verschilPunten, vorige, variant = 'tekst' }: RichtingstekstProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const metVorige = typeof vorige?.percentage === 'number' ? { week: weeknummer(vorige.weekKey), percentage: formatGetal(vorige.percentage, locale) } : null;
  const punten = typeof verschilPunten === 'number' ? verschilPunten : null;

  let kleur = 'text-[var(--foreground-muted)]';
  let vlak = '';
  let teken: string | null = null;
  let tekst = t('qa.rapportkaart.richtingGeenVergelijking');
  if ((richting === 'hoger' || richting === 'lager') && punten !== null) {
    const hoger = richting === 'hoger';
    kleur = hoger ? 'text-[var(--success-text)]' : 'text-[var(--warning-text)]';
    vlak = hoger ? 'bg-[var(--success-light)]' : 'bg-[var(--warning-light)]';
    teken = hoger ? OMHOOG : OMLAAG;
    const opties = { count: punten, punten: formatGetal(punten, locale) };
    tekst = metVorige
      ? t(hoger ? 'qa.rapportkaart.richtingHogerDan' : 'qa.rapportkaart.richtingLagerDan', { ...opties, ...metVorige })
      : t(hoger ? 'qa.rapportkaart.richtingHoger' : 'qa.rapportkaart.richtingLager', opties);
  } else if (richting === 'gelijk') {
    tekst = metVorige ? t('qa.rapportkaart.richtingGelijkAan', metVorige) : t('qa.rapportkaart.richtingGelijk');
  }

  // Zonder richting in het antwoord is er niets te labelen: dan blijft het gewone tekst, ook op de badge-variant.
  const badge = variant === 'badge' && richting !== undefined;
  const klassen = badge
    ? `inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[0.75rem] font-medium ${kleur} ${vlak}`
    : `inline-flex items-baseline gap-1 text-[0.75rem] font-medium ${kleur}`;

  return (
    <span className={klassen} data-testid="werkkwaliteit-richting">
      {badge && <span aria-hidden="true" className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-current" />}
      {teken !== null && <span aria-hidden="true">{teken}</span>}
      {teken !== null && ' '}
      <span>{tekst}</span>
    </span>
  );
}
